import { useState, useRef, useEffect } from 'react';

export function useSpace({
  session,
  userRecord,
  setUserRecord,
  supabase,
  generateId,
  setCurrentSessionId,
  setMessages,
  onInviteSent,
}) {

  // ── INTERNAL STATE ──────────────────────────────────────────────────────────
  const [sharedSessions,       setSharedSessions]       = useState([]);
  const [sharedSpaceLoading,   setSharedSpaceLoading]   = useState(false);
  const [spaceSearch,          setSpaceSearch]          = useState('');
  const [pendingInvites,       setPendingInvites]       = useState([]);
  const [sentInvites,          setSentInvites]          = useState(null);
  const [sentInvitesLoading,   setSentInvitesLoading]   = useState(false);
  const [activeSpaceInfo,      setActiveSpaceInfo]      = useState(null);
  const [showSpaceDisclaimer,  setShowSpaceDisclaimer]  = useState(false);
  const [spaceDisclaimerTarget,setSpaceDisclaimerTarget]= useState(null);
  const [inviteEmail,          setInviteEmail]          = useState('');
  const [inviteSending,        setInviteSending]        = useState(false);
  const [inviteError,          setInviteError]          = useState('');
  const [inviteSuccess,        setInviteSuccess]        = useState('');
  const [inviteSpaceType,      setInviteSpaceType]      = useState('space');
  const [inviteShiftMode,     setInviteShiftMode]     = useState('observer');
  const [inviteSpaceComment,   setInviteSpaceComment]   = useState('');
  const [inviteConsentChecked, setInviteConsentChecked] = useState(false);
  const [inviteSpaceTitle,      setInviteSpaceTitle]      = useState('');
  const [inviteSpaceProfRole,   setInviteSpaceProfRole]   = useState('');
  // Flag set by the Workspace panel so the invite lands in Workspace, not Space.
  // When true, /api/invite writes is_workspace=true + workspace_metadata on
  // the shared_spaces row so the Workspace panel filter picks it up.
  const [inviteIsWorkspace,     setInviteIsWorkspace]     = useState(false);
  const [inviteSpaceArticle,    setInviteSpaceArticle]    = useState('');
  const [inviteSpaceFile,       setInviteSpaceFile]       = useState(null);
  const [inviteSpaceFileError,  setInviteSpaceFileError]  = useState('');
  const [inviteTab,             setInviteTab]             = useState('space');
  const [inviteToShiftEmail,    setInviteToShiftEmail]    = useState('');
  const [inviteToShiftComment,  setInviteToShiftComment]  = useState('');
  const [inviteToShiftStatus,   setInviteToShiftStatus]   = useState('');
  const [inviteToShiftSending,  setInviteToShiftSending]  = useState(false);
  const [inviteToShiftUrl,      setInviteToShiftUrl]      = useState('');
  const realtimeChannelRef = useRef(null);
  const globalChannelRef = useRef(null);

  // ── REALTIME — watch for participant + invitation changes ──
  useEffect(() => {
    if (!session?.user?.id || !supabase) return;
    if (globalChannelRef.current) supabase.removeChannel(globalChannelRef.current);

    const channel = supabase
      .channel('space-changes-global')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'space_participants' }, () => { loadSharedSpace(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'invitations' }, () => { loadSharedSpace(); })
      .subscribe();

    globalChannelRef.current = channel;

    return () => {
      if (globalChannelRef.current) supabase.removeChannel(globalChannelRef.current);
    };
  }, [session?.user?.id]);

  // ── LOAD SHARED SPACES ──────────────────────────────────────────────────────
  async function loadSharedSpace() {
    if (!session) return;
    setSharedSpaceLoading(true);
    try {
      const headers = await getInviteAuthHeaders();

      // Load spaces via server-side API (RLS-safe)
      const spacesRes = await fetch('/api/user/spaces', { headers });
      if (spacesRes.ok) {
        const { spaces } = await spacesRes.json();
        // Compare by serializing key fields — prevents flash on identical data
        setSharedSessions(prev => {
          const newData = spaces || [];
          const key = s => `${s.id}:${(s.participants||[]).length}`;
          const newKey = newData.map(key).join('|');
          const prevKey = prev.map(key).join('|');
          return newKey !== prevKey ? newData : prev;
        });

        // NOTE: incognito space cleanup happens only on explicit leave (closeSpaceSession)
        // Do NOT auto-delete private spaces here — they must survive until the user leaves
      } else {
        setSharedSessions([]);
      }

      // Load pending invites
      const invRes = await fetch('/api/invite', {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'checkInvites', userId: session.user.id }),
      });
      const invData = await invRes.json();
      setPendingInvites(prev => {
        const newData = invData.invites || [];
        const key = i => `${i.id}:${i.status}`;
        const newKey = newData.map(key).join('|');
        const prevKey = prev.map(key).join('|');
        return newKey !== prevKey ? newData : prev;
      });
    } catch (e) { console.error('loadSharedSpace:', e.message); }
    finally { setSharedSpaceLoading(false); }
  }

  // ── DISCLAIMER GATE ─────────────────────────────────────────────────────────
  function gateSpaceEntry(callback) {
    if (userRecord?.shared_space_disclaimer_accepted) {
      callback();
    } else {
      setSpaceDisclaimerTarget(() => callback);
      setShowSpaceDisclaimer(true);
    }
  }

  async function acceptSpaceDisclaimer() {
    try {
      await supabase
        .from('users')
        .update({ shared_space_disclaimer_accepted: true })
        .eq('id', session.user.id);
      if (setUserRecord) {
        setUserRecord(prev => ({ ...prev, shared_space_disclaimer_accepted: true }));
      }
      setShowSpaceDisclaimer(false);
      if (spaceDisclaimerTarget) {
        spaceDisclaimerTarget();
        setSpaceDisclaimerTarget(null);
      }
    } catch (e) { console.error('acceptSpaceDisclaimer:', e.message); }
  }

  // ── OPEN SPACE SESSION ──────────────────────────────────────────────────────
  async function openSpaceSession(space, skipDisclaimer) {
    const enter = async () => {
      try {
        const { data: { session: authSess } } = await supabase.auth.getSession();
        const headers = { 'Authorization': `Bearer ${authSess?.access_token || ''}` };

        // If space object is minimal (from accept), fetch full data
        let fullSpace = space;
        if (!space.otherNames) {
          const spacesRes = await fetch('/api/user/spaces', { headers });
          if (spacesRes.ok) {
            const { spaces } = await spacesRes.json();
            fullSpace = spaces?.find(s => s.id === (space.id || space.space_id)) || space;
          }
        }

        const msgRes = await fetch(`/api/user/messages?sessionId=${encodeURIComponent(fullSpace.id)}`, { headers });
        const msgs = msgRes.ok ? (await msgRes.json()).messages || [] : [];

        setMessages(msgs);
        setCurrentSessionId(fullSpace.id);
        setActiveSpaceInfo(fullSpace);
        localStorage.setItem('shift_last_session', fullSpace.id);
        localStorage.setItem('shift_active_space', JSON.stringify({ id: fullSpace.id, name: fullSpace.name, space_type: fullSpace.space_type }));

        if (realtimeChannelRef.current) {
          supabase.removeChannel(realtimeChannelRef.current);
        }

        const channel = supabase
          .channel(`space:${space.id}`)
          .on('postgres_changes', {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `session_id=eq.${space.id}`,
          }, payload => {
            const msg = payload.new;
            if (msg.sender_id !== session.user.id) {
              setMessages(prev => {
                // Dedup by id — already have this exact row.
                if (prev.some(m => m.id === msg.id)) return prev;

                // Dedup by content match — when the asker's SSE stream
                // for askShift completes, the server inserts a 'shift'
                // row whose realtime echo arrives here. The asker
                // already has a local streaming bubble that just
                // finished; without this check, both the local bubble
                // and the realtime echo would render side-by-side until
                // the next poll reconciles. Adopt the DB id on the
                // matching local bubble instead of appending.
                if (msg.role === 'assistant' || msg.sender_id === 'shift') {
                  const idx = prev.findIndex(m =>
                    m.role === 'assistant' &&
                    typeof m.content === 'string' &&
                    typeof msg.content === 'string' &&
                    (m.content === msg.content ||
                      // mid-stream: local has streamed text but is still
                      // streaming — adopt id; the SSE done event will
                      // also try to rename, both no-ops once ids match.
                      (m.streaming && msg.content.startsWith(m.content.slice(0, 200))))
                  );
                  if (idx >= 0) {
                    const next = [...prev];
                    next[idx] = { ...next[idx], id: msg.id, content: msg.content, streaming: false };
                    return next;
                  }
                }

                return [...prev, {
                  id: msg.id,
                  role: msg.role,
                  content: msg.content,
                  sender_id: msg.sender_id,
                  created_at: msg.created_at,
                }];
              });
            }
          })
          .subscribe();

        realtimeChannelRef.current = channel;
      } catch (e) { console.error('openSpaceSession:', e.message); }
    };
    if (skipDisclaimer) { enter(); } else { gateSpaceEntry(enter); }
  }

  // ── CLOSE SPACE SESSION ─────────────────────────────────────────────────────
  // explicitLeave=true only when user clicks Leave button — triggers incognito wipe
  // Navigation away from chat calls this without explicitLeave — just clears state
  async function closeSpaceSession({ explicitLeave } = {}) {
    const wasIncognito = activeSpaceInfo?.space_type === 'private';
    const closingSpaceId = activeSpaceInfo?.id;

    // Tear down realtime
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }

    // Clear state
    setActiveSpaceInfo(null);
    localStorage.removeItem('shift_active_space');

    // Incognito: full wipe ONLY on explicit leave — not on navigation
    if (explicitLeave && wasIncognito && closingSpaceId) {
      try {
        const headers = await getInviteAuthHeaders();
        await fetch('/api/user/delete-space', { method: 'POST', headers, body: JSON.stringify({ spaceId: closingSpaceId }) });
        setSharedSessions(prev => prev.filter(s => s.id !== closingSpaceId));
      } catch (e) { console.error('Incognito cleanup failed:', e.message); }

      const savedSession = localStorage.getItem('shift_last_session');
      if (savedSession === closingSpaceId) {
        localStorage.removeItem('shift_last_session');
      }
    }
  }

  // ── DELETE SPACE SESSION ────────────────────────────────────────────────────
  async function deleteSpaceSession(spaceId) {
    try {
      const headers = await getInviteAuthHeaders();
      const res = await fetch('/api/user/delete-space', { method: 'POST', headers, body: JSON.stringify({ spaceId }) });
      if (res.ok) setSharedSessions(prev => prev.filter(s => s.id !== spaceId));
    } catch (e) { console.error('deleteSpaceSession:', e.message); }
  }

  async function leaveSpaceSession(spaceId) {
    try {
      const headers = await getInviteAuthHeaders();
      const res = await fetch('/api/user/delete-space', { method: 'POST', headers, body: JSON.stringify({ spaceId, leaveOnly: true }) });
      if (res.ok) setSharedSessions(prev => prev.filter(s => s.id !== spaceId));
    } catch (e) { console.error('leaveSpaceSession:', e.message); }
  }

  // ── INVITE SYSTEM ───────────────────────────────────────────────────────────
  // Helper — get auth headers for API calls
  async function getInviteAuthHeaders() {
    const { data: { session: authSess } } = await supabase.auth.getSession();
    return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authSess?.access_token || ''}` };
  }

  async function sendSharedInvite(args) {
    const email = args?.email ?? inviteEmail;
    const comment = args?.comment ?? inviteSpaceComment;
    const spaceType = args?.spaceType ?? inviteSpaceType;
    const profRole = args?.profRole ?? inviteSpaceProfRole;
    if (!email?.trim()) return;
    setInviteSending(true);
    setInviteError('');
    setInviteSuccess('');
    try {
      const headers = await getInviteAuthHeaders();
      const res = await fetch('/api/invite', {
        method: 'POST', headers,
        body: JSON.stringify({
          action: 'sendInvite',
          inviterId: session.user.id,
          inviteeEmail: email.trim(),
          personalNote: comment?.trim() || null,
          materialText: inviteSpaceArticle?.trim() || null,
          attachedFile: inviteSpaceFile || null,
          spaceType: spaceType || 'space',
          profRole: spaceType === 'professional' ? profRole?.trim() || null : null,
          spaceName: inviteSpaceTitle?.trim() || null,
          shiftMode: inviteShiftMode || 'observer',
          isWorkspace: !!inviteIsWorkspace,
        }),
      });
      if (!res.ok) {
        let errMsg = 'Something went wrong. Please try again.';
        try {
          const errData = await res.json();
          if (errData.error) errMsg = errData.error;
        } catch { /* non-critical */ }
        setInviteError(errMsg);
        return;
      }
      const data = await res.json();
      if (data.success) {
        setInviteSuccess(data.inviteUrl ? `${data.message || 'Invite sent.'} Link: ${data.inviteUrl}` : `Invite sent to ${email.trim()}`);
        setInviteEmail('');
        setInviteSpaceComment('');
        setInviteSpaceArticle('');
        setInviteSpaceFile(null);
        setInviteSpaceProfRole('');
        setInviteConsentChecked(false);
        setInviteSpaceTitle('');
        setInviteShiftMode('observer');
        setTimeout(() => setInviteSuccess(''), 8000);
        await loadSharedSpace();
        await loadSentInvites();
        onInviteSent?.();
      } else { setInviteError(data.error || 'Could not send invite'); }
    } catch (e) {
      setInviteError('Something went wrong: ' + e.message);
      console.error('sendSharedInvite:', e.message);
    } finally { setInviteSending(false); }
  }

  async function acceptSharedInvite(inviteId) {
    // Optimistic — remove from pending immediately
    setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
    try {
      const headers = await getInviteAuthHeaders();
      const res = await fetch('/api/invite', {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'acceptInvite', userId: session.user.id, inviteId }),
      });
      const data = await res.json();
      if (data.success) {
        await loadSharedSpace();
        const spaceId = data.spaceId;
        if (spaceId) openSpaceSession({ id: spaceId, space_id: spaceId });
      }
    } catch (e) { console.error('acceptSharedInvite:', e.message); }
  }

  async function declineSharedInvite(inviteId) {
    // Optimistic — remove from pending immediately
    setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
    try {
      const headers = await getInviteAuthHeaders();
      fetch('/api/invite', {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'declineInvite', inviteId }),
      }).catch(() => {});
    } catch (e) { console.error('declineSharedInvite:', e.message); }
  }

  async function loadSentInvites() {
    setSentInvitesLoading(true);
    try {
      const headers = await getInviteAuthHeaders();
      const res = await fetch('/api/invite', {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'checkSentInvites' }),
      });
      if (res.ok) {
        const data = await res.json();
        setSentInvites(data.invites || []);
      }
    } catch (e) { console.error('loadSentInvites:', e.message); }
    finally { setSentInvitesLoading(false); }
  }

  async function withdrawInvite(inviteId) {
    // Optimistic — remove from sent immediately
    setSentInvites(prev => (prev || []).filter(i => i.id !== inviteId));
    try {
      const headers = await getInviteAuthHeaders();
      fetch('/api/invite', {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'declineInvite', inviteId }),
      }).catch(() => {});
    } catch (e) { console.error('withdrawInvite:', e.message); }
  }

  // ── RETURN ──────────────────────────────────────────────────────────────────
  return {
    // Re-export the supabase client so consumers (e.g. confirm dialogs)
    // can hit auth-protected endpoints without re-importing it.
    supabase,
    // State
    sharedSessions, sharedSpaceLoading,
    spaceSearch, setSpaceSearch,
    pendingInvites, setPendingInvites,
    sentInvites, sentInvitesLoading,
    activeSpaceInfo,
    showSpaceDisclaimer, setShowSpaceDisclaimer,
    inviteEmail, setInviteEmail,
    inviteSending, inviteError, setInviteError, inviteSuccess,
    inviteSpaceType, setInviteSpaceType,
    inviteShiftMode, setInviteShiftMode,
    inviteSpaceComment, setInviteSpaceComment,
    inviteConsentChecked, setInviteConsentChecked,
    inviteSpaceTitle, setInviteSpaceTitle,
    inviteSpaceProfRole, setInviteSpaceProfRole,
    inviteIsWorkspace, setInviteIsWorkspace,
    inviteSpaceArticle, setInviteSpaceArticle,
    inviteSpaceFile, setInviteSpaceFile,
    inviteSpaceFileError, setInviteSpaceFileError,
    inviteTab, setInviteTab,
    inviteToShiftEmail, setInviteToShiftEmail,
    inviteToShiftComment, setInviteToShiftComment,
    inviteToShiftStatus, setInviteToShiftStatus,
    inviteToShiftSending,
    inviteToShiftUrl,
    // Functions
    loadSharedSpace,
    openSpaceSession,
    closeSpaceSession,
    deleteSpaceSession, leaveSpaceSession,
    sendSharedInvite,
    acceptSharedInvite,
    declineSharedInvite,
    loadSentInvites,
    withdrawInvite,
    acceptSpaceDisclaimer,
    gateSpaceEntry,
  };
}